// utils/access.ts
// Central adgangs-logik for Norewyn Campus.
// Brugt af App.tsx (checkAccess wrapper), OccupantList, GuardianBoard, SchedulePopup.

import { Player, RoomData, Wing, RoomAccessRule, WingAccessRule } from '../types';
import { isStaff } from './roles';

export interface AccessContext {
  rooms: Record<string, RoomData>;
  wings: Wing[];
  roomRules: Record<string, RoomAccessRule>;
  wingRules: Record<string, WingAccessRule>;
}

export interface AccessResult {
  hasAccess: boolean;
  reason?: string;
}

/**
 * Tjek om en bruger har adgang til et wing eller room.
 * Logik (uændret fra tidligere App.tsx checkAccess):
 *  1. Staff har altid adgang.
 *  2. Block trumfer alt (også extra-allow).
 *  3. Forseglet (isActive=false) blokerer alle non-staff.
 *  4. extraAllowedUserIds trumfer wing-arv-deny.
 *  5. Legacy room-whitelist trumfer wing-arv hvis sat.
 *  6. Ellers wing-arv (OR over alle wings room'et hører til).
 */
export const checkAccessFor = (
  user: Player | null,
  targetId: string,
  ctx: AccessContext
): AccessResult => {
  if (!user) return { hasAccess: false };
  if (isStaff(user.role)) return { hasAccess: true };

  const { rooms, wings, roomRules, wingRules } = ctx;

  const passesRule = (rule: {
    allowedUsers?: string[];
    allowedClassrooms?: string[];
    allowedAges?: string[];
    allowedGuilds?: string[];
    logic: 'AND' | 'OR';
  } | undefined): boolean => {
    if (!rule) return true;
    const checks = {
      user: rule.allowedUsers?.includes(user.id),
      classroom: (user.classIds || []).some((c: string) => rule.allowedClassrooms?.includes(c) || false),
      age: rule.allowedAges?.includes(user.ageGroup || ''),
      guild: rule.allowedGuilds?.includes(user.guild || ''),
    };
    const anySet = !!(
      rule.allowedUsers?.length ||
      rule.allowedClassrooms?.length ||
      rule.allowedAges?.length ||
      rule.allowedGuilds?.length
    );
    if (!anySet) return true;
    if (rule.logic === 'AND') {
      return Boolean(
        (!rule.allowedUsers?.length || checks.user) &&
        (!rule.allowedClassrooms?.length || checks.classroom) &&
        (!rule.allowedAges?.length || checks.age) &&
        (!rule.allowedGuilds?.length || checks.guild)
      );
    }
    return Object.values(checks).some(v => v === true);
  };

  const isWing = wings.some(w => w.id === targetId);
  if (isWing) {
    const wingDoc: any = wings.find(w => w.id === targetId);
    if (wingDoc && wingDoc.isActive === false) return { hasAccess: false, reason: 'Vingen er magisk forseglet lige nu.' };
    const wRule = wingRules[targetId];
    if (!wRule) return { hasAccess: true };
    // seglet laeses nu fra wing-dokumentet ovenfor
    return passesRule(wRule)
      ? { hasAccess: true }
      : { hasAccess: false, reason: "Du har ikke adgang til denne vinge." };
  }

  const room = rooms[targetId];
  if (!room) return { hasAccess: true };
  const rRule = roomRules[targetId];

  if (rRule?.blockedUserIds?.includes(user.id)) {
    return { hasAccess: false, reason: "Du er blokeret fra dette rum." };
  }
  if ((room as any).isActive === false) {
    return { hasAccess: false, reason: "Døren er magisk forseglet lige nu." };
  }
  if (rRule?.extraAllowedUserIds?.includes(user.id)) {
    return { hasAccess: true };
  }
  const hasLegacy = !!(
    rRule?.allowedUsers?.length ||
    rRule?.allowedClassrooms?.length ||
    rRule?.allowedAges?.length ||
    rRule?.allowedGuilds?.length
  );
  if (hasLegacy) {
    return passesRule(rRule)
      ? { hasAccess: true }
      : { hasAccess: false, reason: "Du har ikke adgang til denne dør." };
  }
  const wingIds = room.wingIds || [];
  if (wingIds.length === 0) return { hasAccess: true };
  const anyWingGrants = wingIds.some(wId => {
    const wDoc: any = wings.find(w => w.id === wId);
    if (wDoc && wDoc.isActive === false) return false;
    const wr = wingRules[wId];
    if (!wr) return true;
    // seglet laeses nu fra wing-dokumentet ovenfor
    return passesRule(wr);
  });
  return anyWingGrants
    ? { hasAccess: true }
    : { hasAccess: false, reason: "Du har ikke adgang til denne dør." };
};
